// v79.159 — Android fullscreen video: hide the Capacitor status bar.
//
// Founder report (Feb 2026): "when YouTube or any video is played in
// Android app, the blue Capacitor header (Android status bar) blocks
// fullscreen playback."
//
// Root cause: HTML5 `<video>` elements enter fullscreen via the
// browser's Fullscreen API, but the Android Capacitor WebView still
// overlays the app's status bar (blue) on top of the fullscreen
// surface. The video shows behind the status bar → not truly
// edge-to-edge.
//
// Fix: subscribe globally to `fullscreenchange` (and its webkit
// equivalent for older WebViews) and call `StatusBar.hide()` /
// `StatusBar.show()` around fullscreen state transitions. Also
// hides the Android nav bar via `overlaysWebView(true)` so the
// media truly fills the surface. Works in the browser (no-op — plugin
// stubs on web) and on Android Capacitor builds.
//
// Called once from `src/index.js` at boot.

import { Capacitor } from "@capacitor/core";

let installed = false;

async function loadStatusBar() {
    if (!Capacitor.isNativePlatform()) return null;
    try {
        const mod = await import("@capacitor/status-bar");
        return mod.StatusBar;
    } catch {
        return null;
    }
}

export function installAndroidFullscreenVideoFix() {
    if (installed) return;
    installed = true;
    // Only wire the listener when the app runs inside Capacitor. In a
    // regular browser, HTML5 fullscreen already covers the OS chrome —
    // no status bar to hide.
    if (typeof document === "undefined") return;

    let StatusBar = null;
    let originalShown = true; // Assume visible at boot.
    loadStatusBar().then((sb) => { StatusBar = sb; });

    const isFullscreenNow = () => Boolean(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
    );

    const handler = async () => {
        if (!StatusBar) return;
        try {
            if (isFullscreenNow()) {
                await StatusBar.hide();
                // Also let the WebView paint under any leftover system
                // insets — belt and braces on Android 11+ WebViews.
                try { await StatusBar.setOverlaysWebView({ overlay: true }); } catch { /* no-op on iOS */ }
                originalShown = false;
            } else if (!originalShown) {
                await StatusBar.show();
                try { await StatusBar.setOverlaysWebView({ overlay: false }); } catch { /* no-op on iOS */ }
                originalShown = true;
            }
        } catch {
            // StatusBar plugin unavailable / not installed on this
            // platform — silently no-op.
        }
    };

    document.addEventListener("fullscreenchange", handler, { passive: true });
    document.addEventListener("webkitfullscreenchange", handler, { passive: true });
    document.addEventListener("mozfullscreenchange", handler, { passive: true });
    document.addEventListener("MSFullscreenChange", handler, { passive: true });
}
